// src/components/SearchBar.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiHome, FiPackage, FiTruck, FiHash } from 'react-icons/fi';
import { FaTicketAlt, FaHardHat } from 'react-icons/fa';
import { useApi } from '../hooks/useApi';

const SearchBar = ({ tickets = [], communities = [], technicians = [], inventory = [], sites = [] }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [focusedIndex, setFocusedIndex] = useState(0);

    const searchRef = useRef(null);
    const resultsContainerRef = useRef(null);
    const inputRef = useRef(null); 
    const overlayRef = useRef(null);
    const navigate = useNavigate();
    const callApi = useApi();

    // --- DISCORD-STYLE TAB AUTOCOMPLETE LOGIC ---
    const AUTOCOMPLETE_TERMS = [
        ':com', ':tic', ':tech', ':inv', ':truck', ':office',
        'open', 'closed', 'active', 'inactive', 'pending', 'overdue',
        'critical', 'high', 'medium', 'low', 'normal',
        'in progress', 'waiting on client', 'waiting on parts'
    ];
    
    let suggestion = '';

    // Safely extract the current text segment (everything after the last comma)
    const segmentsArray = query.split(',');
    const lastSegment = segmentsArray[segmentsArray.length - 1]; 
    
    let searchWord = '';
    
    // This isolates the filter text from any leading command
    const commandMatch = lastSegment.match(/^\s*(:[a-z]+)\s+(.*)$/i);
    
    if (commandMatch) {
        searchWord = commandMatch[2]; 
    } else {
        searchWord = lastSegment.trimStart(); 
    }

    searchWord = searchWord.toLowerCase();

    // If they are actually typing a word, look for a match
    if (searchWord.length > 0) {
        const matchedTerm = AUTOCOMPLETE_TERMS.find(term => term.startsWith(searchWord));
        
        if (matchedTerm) {
            const isCommand = matchedTerm.startsWith(':');
            const suffix = isCommand ? ' ' : ', '; 
            
            if (matchedTerm !== searchWord) {
                suggestion = matchedTerm.substring(searchWord.length) + suffix;
            } else {
                suggestion = suffix;
            }
        }
    }

    // Reset focused index when results change
    useEffect(() => {
        setFocusedIndex(0);
    }, [results]);

    // Sync scrolling between the input and the ghost text overlay
    const handleScroll = (e) => {
        if (overlayRef.current) {
            overlayRef.current.scrollLeft = e.target.scrollLeft;
        }
    };

    // --- SEARCH LOGIC ---
    useEffect(() => {
        const rawQuery = query.toLowerCase();

        const activeFilters = {
            community: [], ticket: [], technician: [], inventory: [], truck: [], office: [], general: []
        };

        const segments = rawQuery.split(',').map(s => s.trim()).filter(s => s !== undefined);
        let currentPrefix = 'general';

        // Add these variables before the segments.forEach loop
        let filterByAidenBelcherTickets = false;
        let technicianNameForTicketSearch = 'aiden belcher'; // Standardize the name for comparison
        
        segments.forEach(segment => {
            let text = segment;
            let isNewPrefix = false;

            if (segment.startsWith(':com')) { currentPrefix = 'community'; text = segment.substring(4).trim(); isNewPrefix = true; }
            else if (segment.startsWith(':tic')) { currentPrefix = 'ticket'; text = segment.substring(4).trim(); isNewPrefix = true; }
            else if (segment.startsWith(':tech')) {
                currentPrefix = 'technician';
                text = segment.substring(5).trim();
                isNewPrefix = true;
                if (text.toLowerCase() === technicianNameForTicketSearch) {
                    filterByAidenBelcherTickets = true;
                    // Do NOT clear text here, as :tech aiden belcher should still match technician profiles
                }
            }
            else if (segment.startsWith(':inv')) { currentPrefix = 'inventory'; text = segment.substring(4).trim(); isNewPrefix = true; }
            else if (segment.startsWith(':truck')) { currentPrefix = 'truck'; text = segment.substring(6).trim(); isNewPrefix = true; }
            else if (segment.startsWith(':office')) { currentPrefix = 'office'; text = segment.substring(7).trim(); isNewPrefix = true; }

            if (text) activeFilters[currentPrefix].push(text);
            else if (isNewPrefix) activeFilters[currentPrefix].push('');
        });

        const hasAnyPrefix = Object.keys(activeFilters).some(k => k !== 'general' && activeFilters[k].length > 0);

        if (!hasAnyPrefix && rawQuery.length < 2) {
            setResults([]);
            return;
        }

        const performSearch = async () => {
            const foundResults = [];

            const textMatchesAnyField = (q, ...fields) => {
                if (!q) return true; 
                return fields.some(field => field && String(field).toLowerCase().includes(q));
            };

            // 1. COMMUNITIES
            const shouldSearchCommunities = (activeFilters.community.length > 0 && activeFilters.inventory.length === 0 && activeFilters.ticket.length === 0 && activeFilters.technician.length === 0 && activeFilters.truck.length === 0 && activeFilters.office.length === 0) || !hasAnyPrefix;
            if (shouldSearchCommunities) {
                const qArray = activeFilters.community.length > 0 ? activeFilters.community : activeFilters.general;
                communities.forEach(community => {
                    if (qArray.some(q => q === '' || textMatchesAnyField(q, community.name, community.address?.city))) {
                        foundResults.push({ id: community._id, type: 'Community', title: community.name, subtitle: community.address?.city || 'Community', path: `/customers/${community._id}`, icon: <FiHome />, sortOrder: 10 });
                    }
                });
            }

            // 2. TICKETS
            const shouldSearchTickets = activeFilters.ticket.length > 0 || (!hasAnyPrefix && activeFilters.truck.length === 0 && activeFilters.office.length === 0);
            if (shouldSearchTickets) {
                tickets.forEach(ticket => {
                    let isValid = false;
                    if (hasAnyPrefix) {
                        let ticketMatch = activeFilters.ticket.length > 0 && activeFilters.ticket.every(q => q === '' || textMatchesAnyField(q, ticket.subject, ticket._id, ticket.status, ticket.priority));
                        let clientMatch = activeFilters.community.length > 0 && activeFilters.community.some(q => q === '' || textMatchesAnyField(q, ticket.client));
                        if (ticketMatch && clientMatch) isValid = true;
                    } else {
                        isValid = activeFilters.general.every(q => q === '' || textMatchesAnyField(q, ticket.subject, ticket._id, ticket.status, ticket.priority, ticket.client));
                        if (isValid && ticket.status === 'Closed' && !activeFilters.general.some(q => textMatchesAnyField(q, ticket.subject, ticket._id, ticket.status, ticket.priority))) {
                            isValid = false;
                        }
                    }
                    if (isValid) {
                        foundResults.push({ id: ticket._id, type: 'Ticket', title: ticket.subject, subtitle: `${ticket.client} • Status: ${ticket.status}`, path: `/tickets/${ticket._id}`, icon: <FaTicketAlt />, sortOrder: 20 });
                    }
                });
            }

            // 3. TECHNICIANS
            const shouldSearchTechnicians = activeFilters.technician.length > 0 || (!hasAnyPrefix && activeFilters.truck.length === 0 && activeFilters.office.length === 0);
            if (shouldSearchTechnicians) {
                technicians.forEach(technician => {
                    let isValid = hasAnyPrefix 
                        ? (activeFilters.technician.length > 0 && activeFilters.technician.every(q => q === '' || textMatchesAnyField(q, technician.name, technician.email, technician.status, technician.skillLevel, ...(technician.specializations || []))))
                        : activeFilters.general.every(q => q === '' || textMatchesAnyField(q, technician.name, technician.email, technician.status, technician.skillLevel, ...(technician.specializations || [])));

                    if (isValid) {
                        foundResults.push({ id: technician._id, type: 'Technician', title: technician.name, subtitle: `${technician.email} • Status: ${technician.status}`, path: `/technicians/${technician._id}`, icon: <FaHardHat />, sortOrder: 30 });
                    }
                });
            }

            // 4. INVENTORY (Now properly filters by :truck and :office)
            const shouldSearchInventory = activeFilters.inventory.length > 0 || activeFilters.truck.length > 0 || activeFilters.office.length > 0 || !hasAnyPrefix;
            if (shouldSearchInventory && inventory && sites) {
                const inventoryMatches = [];

                inventory.forEach(item => {
                    let matchesItem = hasAnyPrefix && activeFilters.inventory.length > 0 
                        ? activeFilters.inventory.every(q => q === '' || textMatchesAnyField(q, item.name, item.sku, item.category))
                        : true;

                    let commName = '';
                    if (item.ownerType === 'Community') {
                        const comm = communities.find(c => c._id === item.ownerId);
                        commName = comm ? comm.name : '';
                    }

                    let matchesComm = true;
                    if (activeFilters.community.length > 0) {
                        if (item.ownerType !== 'Community') {
                            matchesComm = false;
                        } else {
                            matchesComm = activeFilters.community.some(q => q === '' || textMatchesAnyField(q, commName));
                        }
                    }

                    let matchesTruck = true;
                    if (activeFilters.truck.length > 0) {
                        if (item.ownerType !== 'Site') {
                            matchesTruck = false;
                        } else {
                            const site = sites.find(s => s._id === item.ownerId);
                            if (!site || site.type === 'Office') {
                                matchesTruck = false;
                            } else {
                                matchesTruck = activeFilters.truck.some(q => q === '' || textMatchesAnyField(q, site.name));
                            }
                        }
                    }

                    let matchesOffice = true;
                    if (activeFilters.office.length > 0) {
                        if (item.ownerType !== 'Site') {
                            matchesOffice = false;
                        } else {
                            const site = sites.find(s => s._id === item.ownerId);
                            if (!site || site.type !== 'Office') {
                                matchesOffice = false;
                            } else {
                                matchesOffice = activeFilters.office.some(q => q === '' || textMatchesAnyField(q, site.name));
                            }
                        }
                    }

                    let isValid = hasAnyPrefix 
                        ? (matchesItem && matchesComm && matchesTruck && matchesOffice)
                        : activeFilters.general.every(q => q === '' || textMatchesAnyField(q, item.name, item.sku, item.category, commName));

                    if (isValid && item.quantity > 0) {
                        let locationName = 'Unknown';
                        let priority = 3;
                        let navPath = '/inventory';
                        let icon = <FiPackage />;

                        if (item.ownerType === 'Site') {
                            const site = sites.find(s => s._id === item.ownerId);
                            if (site) {
                                if (site.type === 'Office') {
                                    locationName = `Office: ${site.name}`;
                                    priority = 0; 
                                    icon = <FiHome />;
                                } else {
                                    locationName = `Truck: ${site.name}`;
                                    priority = 1; 
                                    icon = <FiTruck />;
                                }
                                navPath = `/inventory?siteId=${site._id}`;
                            }
                        } else if (item.ownerType === 'Community') {
                            locationName = `Community: ${commName}`;
                            priority = 2; 
                            navPath = `/inventory?communityId=${item.ownerId}`;
                        }

                        inventoryMatches.push({
                            id: item._id, type: 'Inventory', title: item.name, subtitle: `${locationName} (Qty: ${item.quantity})`, sku: item.sku, path: navPath, icon: icon, invPriority: priority, sortOrder: 5,
                        });
                    }
                });

                inventoryMatches.sort((a, b) => a.invPriority - b.invPriority);
                foundResults.push(...inventoryMatches);

                // 5. SEARCH SERIAL NUMBERS (API) - Adding truck/office support here too
                const apiQueryString = (activeFilters.inventory.length > 0 ? activeFilters.inventory : activeFilters.general).filter(q => q !== '').join(' ');
                
                // If they specifically searched a truck/office but no inventory string, fetch all for that site if possible, 
                // but API requires a query string, so we'll only trigger SN search if there's an actual item query.
                if (apiQueryString.length >= 2) {
                    try {
                        const snRes = await callApi(`inventory/search-units?q=${encodeURIComponent(apiQueryString)}`);
                        if (snRes.success && Array.isArray(snRes.data)) {
                            snRes.data.forEach(unit => {
                                let locationName = 'Unknown';
                                let navPath = '/inventory';
                                let queryParams = '';
                                let matchesSiteFilters = true;

                                if (unit.ownerType === 'Site') {
                                    if (activeFilters.community.length > 0) matchesSiteFilters = false; 
                                    // eslint-disable-next-line eqeqeq
                                    const site = sites.find(s => s._id == unit.ownerId);
                                    if (site) {
                                        locationName = `${site.type}: ${site.name}`;
                                        queryParams = `?siteId=${unit.ownerId}`;
                                        
                                        // Filter against :truck and :office
                                        if (activeFilters.office.length > 0 && site.type !== 'Office') matchesSiteFilters = false;
                                        if (activeFilters.truck.length > 0 && site.type === 'Office') matchesSiteFilters = false;
                                        
                                        if (activeFilters.office.length > 0 && site.type === 'Office') {
                                            matchesSiteFilters = activeFilters.office.some(q => q === '' || textMatchesAnyField(q, site.name));
                                        }
                                        if (activeFilters.truck.length > 0 && site.type !== 'Office') {
                                            matchesSiteFilters = activeFilters.truck.some(q => q === '' || textMatchesAnyField(q, site.name));
                                        }
                                    }
                                } else if (unit.ownerType === 'Community') {
                                    if (activeFilters.truck.length > 0 || activeFilters.office.length > 0) matchesSiteFilters = false;
                                    
                                    // eslint-disable-next-line eqeqeq
                                    const comm = communities.find(c => c._id == unit.ownerId);
                                    if (activeFilters.community.length > 0) {
                                        matchesSiteFilters = activeFilters.community.some(q => q === '' || textMatchesAnyField(q, comm ? comm.name : ''));
                                    }
                                    if (comm) {
                                        locationName = `Community: ${comm.name}`;
                                        queryParams = `?communityId=${unit.ownerId}`;
                                    }
                                }

                                if (matchesSiteFilters) {
                                    foundResults.push({
                                        id: unit._id, type: 'Unit', title: `${unit.name} (SN: ${unit.serialNumber})`, subtitle: `${locationName} - ${unit.status}`, path: navPath + queryParams, icon: <FiHash />, sortOrder: 1, state: { autoOpenUnitSerial: unit.serialNumber }
                                    });
                                }
                            });
                        }
                    } catch (error) {
                        console.error("SN Search Error:", error);
                    }
                }
            }

            foundResults.sort((a, b) => a.sortOrder - b.sortOrder);
            setResults(foundResults.slice(0, 15));
        };

        performSearch();
    }, [query, tickets, communities, technicians, inventory, sites, callApi]);

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setQuery('');
                setResults([]);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Arrow key scroll into view
    useEffect(() => {
        if (focusedIndex >= 0 && resultsContainerRef.current) {
            const activeElement = resultsContainerRef.current.children[focusedIndex];
            if (activeElement) activeElement.scrollIntoView({ block: 'nearest' });
        }
    }, [focusedIndex]);

    const handleResultClick = (result) => {
        navigate(result.path, { state: result.state });
        setQuery('');
        setResults([]);
        setFocusedIndex(0);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Tab') {
            e.preventDefault(); 
            
            // 1. Ghost Text Prefix Autocomplete
            if (suggestion) {
                setQuery(query + suggestion);
                return;
            }

            // 2. Dropdown Item Autocomplete
            if (results.length > 0 && focusedIndex >= 0 && focusedIndex < results.length) {
                const resultToFill = results[focusedIndex];
                const parts = query.split(',');
                const lastPart = parts.pop() || '';
                const baseQuery = parts.length > 0 ? parts.join(',') + ',' : '';
                
                let newLastPart = '';
                const prefixMatch = lastPart.match(/^(\s*:[a-z]+(?:\s+|$))(.*)$/i);
                
                if (prefixMatch) {
                    let prefix = prefixMatch[1];
                    if (!prefix.endsWith(' ')) prefix += ' '; 
                    newLastPart = prefix + resultToFill.title;
                } else {
                    const spaceMatch = lastPart.match(/^(\s*)/);
                    const spaces = spaceMatch && spaceMatch[1] ? spaceMatch[1] : (baseQuery ? ' ' : '');
                    newLastPart = spaces + resultToFill.title;
                }
                
                setQuery(baseQuery + newLastPart + ', ');
                return;
            }
        }

        if (results.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault(); 
            setFocusedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const indexToSelect = (focusedIndex >= 0 && focusedIndex < results.length) ? focusedIndex : 0;
            handleResultClick(results[indexToSelect]);
        } else if (e.key === 'Escape') {
            setQuery('');
            setResults([]);
            setFocusedIndex(0);
        }
    };

    return (
        <div className="header-search-wrapper" ref={searchRef}>
            <div className="search-input-wrapper">
                <FiSearch className="search-icon" />
                
                <div className="ghost-text-overlay" ref={overlayRef}>
                    <span className="ghost-text-hidden">{query}</span>
                    {suggestion && (
                        <span className="ghost-text-visible">{suggestion}</span>
                    )}
                </div>

                <input
                    ref={inputRef}
                    type="search"
                    className="search-input"
                    placeholder="Search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onScroll={handleScroll} 
                />
            </div>
            
            {results.length > 0 && (
                <div className="search-results-dropdown" ref={resultsContainerRef}>
                    {results.map((result, index) => (
                        <div 
                            key={`${result.type}-${result.id}`} 
                            className={`search-result-item ${focusedIndex === index ? 'focused' : ''}`} 
                            onClick={() => handleResultClick(result)}
                            onMouseEnter={() => setFocusedIndex(index)} 
                        >
                            <div className="result-item-icon">{result.icon}</div>
                            <div className="result-item-info">
                                <p className="result-item-title">{result.title}</p>
                                <p className="result-item-subtitle">
                                    {result.type === 'Inventory' && result.sku ? <span style={{ marginRight: '5px', fontWeight: 'bold' }}>{result.sku}</span> : null}
                                    {result.subtitle}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SearchBar;